import { Check, X } from "lucide-react";
import { useScrollReveal } from "@/hooks/useScrollReveal";

const CitationGap = () => {
  const ref = useScrollReveal();
  const cardsRef = useScrollReveal<HTMLDivElement>({ stagger: true });

  return (
    <section ref={ref} className="pt-10 pb-10 md:pt-12 md:pb-16 px-4">
      <div className="max-w-4xl mx-auto">
        <h2 className="text-2xl md:text-3xl font-bold text-foreground mb-4">
          Ranking on Google doesn't mean AI recommends you
        </h2>
        <p className="text-muted-foreground leading-relaxed mb-4 max-w-2xl">
          Google and AI platforms choose sources differently. A brand can rank #1 on Google
          and still not get recommended by ChatGPT. Research shows that 37% of domains cited
          by AI platforms are unique. They don't overlap with Google's organic results.
        </p>
        <p className="text-muted-foreground leading-relaxed mb-2 max-w-2xl">
          This is the <strong className="text-foreground">Citation Gap</strong>.
        </p>
        <p className="text-sm font-serif italic text-muted-foreground mb-10">
          Zhang et al., 2025: Source Coverage and Citation Bias in AI Search
        </p>

        {/* Citation Gap Visual */}
        <div ref={cardsRef} className="grid md:grid-cols-2 gap-6 mb-6 items-stretch">
          {/* Google Card - Success */}
          <div className="border-2 border-border bg-card shadow-sm transition-all duration-200 hover:-translate-y-1 hover:shadow-md flex flex-col h-full">
            <div className="p-6 space-y-3">
              <div className="flex items-center gap-2 text-xs font-mono text-muted-foreground uppercase tracking-wider">
                <div className="w-3 h-3 rounded-full bg-muted-foreground/30" />
                <span>Search Results</span>
              </div>
              <div className="border border-border p-4 bg-background">
                <div className="flex items-start gap-3">
                  <Check className="w-5 h-5 text-success shrink-0 mt-0.5" />
                  <div>
                    <p className="font-semibold text-foreground text-sm">yoursite.com</p>
                    <p className="text-xs text-muted-foreground mt-1">Ranking #1 for "best [your product]"</p>
                    <p className="text-xs text-muted-foreground mt-2">Organic result · Page 1 · High authority</p>
                  </div>
                </div>
              </div>
            </div>
            <div className="mt-auto border-t border-success/30 bg-success/5 px-3 py-2">
              <p className="text-sm font-semibold text-success flex items-center gap-1.5">
                <Check className="w-4 h-4" />
                Found on Google
              </p>
            </div>
          </div>

          {/* AI Card - Failure */}
          <div className="border-2 border-border bg-card shadow-sm transition-all duration-200 hover:-translate-y-1 hover:shadow-md flex flex-col h-full">
            <div className="p-6 space-y-3">
              <div className="flex items-center gap-2 text-xs font-mono text-muted-foreground uppercase tracking-wider">
                <div className="w-3 h-3 rounded-full bg-muted-foreground/30" />
                <span>AI Response</span>
              </div>
              <div className="border border-border p-4 bg-background">
                <p className="text-sm text-muted-foreground leading-relaxed">
                  "Here are the top recommendations:
                  <br />1. Competitor A
                  <br />2. Competitor B
                  <br />3. Competitor C..."
                </p>
              </div>
            </div>
            <div className="mt-auto border-t border-error/30 bg-error/5 px-3 py-2">
              <p className="text-sm font-semibold text-error flex items-center gap-1.5">
                <X className="w-4 h-4" />
                Your brand not recommended
              </p>
            </div>
          </div>
        </div>

        <p className="text-center text-sm font-semibold text-muted-foreground">
          Same brand. Same keyword. Different results.
        </p>
      </div>
    </section>
  );
};

export default CitationGap;
